import { css } from '@emotion/react';
import { useHover } from '@react-aria/interactions';
import { FunctionComponent } from 'react';

import { ChevronDownIcon } from '~src/icons';
import { CaretRightIcon } from '~src/icons/caret-right';
import { Colors, colors } from '~src/theme/colors';
import { fontStyles } from '~src/theme/fontStyles';

export interface TextProperties {
  text: React.ReactNode;
  textStyle?: 'bold' | 'normal';
  color: Colors[keyof Colors];
}

export interface OpenModalButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  title: TextProperties;
  description?: TextProperties;
  ariaLabel?: string;
  alterIcon?: boolean;
}

export const OpenModalButton: FunctionComponent<OpenModalButtonProps> = ({
  title,
  description,
  ariaLabel,
  alterIcon,
  ...buttonProps
}) => {
  const { hoverProps, isHovered } = useHover({});

  return (
    // OpenModalButtonGroup relies on this being the top element.
    // Its border is changed using :first/last-child element
    <button
      css={styles.buttonStyle(isHovered)}
      {...buttonProps}
      {...hoverProps}
      className="focus-visible-outline"
      aria-label={ariaLabel}
    >
      <div css={styles.rightSideStyle}>
        <div css={styles.titleWrapperStyle}>
          <span
            css={{
              color: title.color,
              fontVariationSettings: `"wght" ${title.textStyle === 'bold' ? 500 : 400}`,
            }}
          >
            {title.text}
          </span>
        </div>
        {description && description.text && (
          <span
            css={{
              color: description.color,
              fontVariationSettings: `"wght" ${description.textStyle === 'bold' ? 500 : 400}`,
            }}
          >
            {description.text}
          </span>
        )}
      </div>
      {alterIcon ? <ChevronDownIcon /> : <CaretRightIcon />}
    </button>
  );
};

const styles = {
  buttonStyle: (hovered?: boolean) =>
    css({
      textAlign: 'left',
      position: 'relative',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '60px',
      width: '100%',
      background: colors.white,
      border: `1px solid ${hovered ? colors.neutral75 : colors.neutral85}`,
      borderRadius: '2px',
      padding: '10px 16px',
      cursor: 'pointer',
      ...fontStyles.px12_16,
    }),
  rightSideStyle: css({
    paddingRight: '16px',
  }),
  titleWrapperStyle: css({
    display: 'flex',
    alignItems: 'center',
    columnGap: '5px',
    ...fontStyles.px12_18,
  }),
};
